import { Typography } from 'antd';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BRAND_COLORS } from '../../../../../../constants/styles';
import { METRIC_HIGHER_IS_BETTER, STATUS_COLORS } from '../../../../constants';
import { dropoutHistoryConfig } from '../../../config/metricHistory';
import { IDropoutDetailsData, IModalFilterContext } from '../../../types';
import { formatActiveStatuses } from '../../../utils/formatActiveStatuses';
import { ExplainerBox } from '../../shared/ExplainerBox';
import { FormulaBox } from '../../shared/FormulaBox';
import { FormulaLine } from '../../shared/FormulaLine';
import { MetricDetailsModal } from '../../shared/MetricDetailsModal';
import { MetricHistoryChart } from '../../shared/MetricHistoryChart';
import { MetricsDivider } from '../../shared/MetricsDivider';
import { StatRow } from '../../shared/StatRow';
import { ThresholdLegend } from '../../shared/ThresholdLegend';

const { Text } = Typography;

interface IDropoutDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  data: IDropoutDetailsData | null;
  filter: IModalFilterContext;
  historyDataCutoff?: string;
}

export const DropoutDetailsModal: FC<IDropoutDetailsModalProps> = ({
  visible,
  onClose,
  data,
  filter,
  historyDataCutoff,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.dropoutModal');
  if (!data) return null;

  return (
    <MetricDetailsModal
      visible={visible}
      onClose={onClose}
      data={data}
      title={t('title')}
      tooltip={t('tooltip', {
        statuses: formatActiveStatuses(filter.activeDonorStatuses),
      })}
      sectionLabel={t('sectionThisQuarter')}
      headerValue={`${data.dropoutRate}%`}
      explainer={
        <ExplainerBox>
          <strong style={{ color: '#fff' }}>{t('explainerTerm')}</strong>{' '}
          {t('explainerDef')}
          <br />
          <Text
            style={{
              color: BRAND_COLORS.secondary.lightGray,
              fontSize: 11,
              marginTop: 4,
            }}
          >
            {t('explainerStatusChange', {
              statuses: formatActiveStatuses(filter.activeDonorStatuses),
            })}
          </Text>
        </ExplainerBox>
      }
      historySection={
        <MetricHistoryChart
          config={dropoutHistoryConfig}
          live={data}
          filter={filter}
          extra={{ historyDataCutoff }}
          translationNs="dashboard.RecruiterDashboard.dropoutModal"
          isQuarterly
        />
      }
    >
      <StatRow label={t('statActiveAtStart')} value={data.activeAtStart} />
      <StatRow
        label={t('statDropouts')}
        value={`−${data.dropoutCount}`}
        color={STATUS_COLORS.delta.negative}
        indent
      />
      <MetricsDivider />
      <FormulaBox>
        <FormulaLine>{t('formula')}</FormulaLine>
        <FormulaLine marginTop={4}>
          = {data.dropoutCount} ÷ {data.activeAtStart} × 100{' = '}
          <span style={{ color: data.statusColor, fontWeight: 700 }}>
            {data.dropoutRate}%
          </span>
        </FormulaLine>
      </FormulaBox>
      <ThresholdLegend
        breakpoints={data.breakpoints}
        higherIsBetter={METRIC_HIGHER_IS_BETTER.dropout}
      />
    </MetricDetailsModal>
  );
};
